import { useLazyQuery, useMutation } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import BulkSendProgressPanel from "./BulkSendProgressPanel";
import EmailsInput from "./EmailsInput";
import {
  ADD_MEMBERS,
  type BulkActionResult,
  MATCH_MEMBERS,
  MAX_BULK_RECIPIENTS,
  type MatchMembersResult,
} from "./helpers";

type ChannelType = "group_chat" | "public_channel";
type AddAction = "add" | "invite";

const AddMembersPanel: React.FC = () => {
  const [emails, setEmails] = useState<string[]>([]);
  const [channelType, setChannelType] = useState<ChannelType>("group_chat");
  const [action, setAction] = useState<AddAction>("add");
  const [channelId, setChannelId] = useState<string>("");
  const [match, setMatch] = useState<MatchMembersResult | null>(null);
  const [result, setResult] = useState<BulkActionResult | null>(null);
  const [error, setError] = useState<string>("");

  const [runMatch, { loading: matching }] = useLazyQuery(MATCH_MEMBERS, {
    fetchPolicy: "network-only",
  });
  const [runAdd, { loading: adding }] = useMutation(ADD_MEMBERS);

  // One request may not carry more than the server accepts; say so here,
  // while the list is still a list she can trim, rather than on send.
  const tooManyEmails = emails.length > MAX_BULK_RECIPIENTS;

  const handleMatch = async (): Promise<void> => {
    setError("");
    setResult(null);
    try {
      const { data } = await runMatch({ variables: { input: { emails } } });
      setMatch(data?.chatAutomationsMatchMembers ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong while matching.");
    }
  };

  const handleAdd = async (): Promise<void> => {
    if (!match) {
      return;
    }
    setError("");
    try {
      const { data } = await runAdd({
        variables: {
          input: {
            channelType,
            action,
            channelId,
            emails: match.matched.map((member) => member.email),
          },
        },
      });
      setResult(data?.chatAutomationsAddMembers ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Something went wrong while adding.");
    }
  };

  const actionLabel = action === "invite" ? "Invite" : "Add";

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="subtitle1" gutterBottom>
        Add or invite members to a channel
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            select
            label="Channel type"
            value={channelType}
            onChange={(event) => setChannelType(event.target.value as ChannelType)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="group_chat">Group Chat</MenuItem>
            <MenuItem value="public_channel">Public Channel</MenuItem>
          </TextField>
          <TextField
            select
            label="Action"
            value={action}
            onChange={(event) => setAction(event.target.value as AddAction)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="add">Add directly</MenuItem>
            <MenuItem value="invite">Invite</MenuItem>
          </TextField>
        </Stack>

        <TextField
          label="Channel ID"
          value={channelId}
          onChange={(event) => setChannelId(event.target.value)}
          placeholder="e.g. 1744043355695"
          helperText="The number at the end of the channel's URL in Hey Lady."
          fullWidth
        />

        <EmailsInput
          emails={emails}
          onChange={setEmails}
          onReset={() => {
            setMatch(null);
            setResult(null);
            setError("");
          }}
        />
        {tooManyEmails && (
          <Alert severity="error">
            That is {emails.length} emails. One run can add at most {MAX_BULK_RECIPIENTS} members —
            split the list and do it in a few passes.
          </Alert>
        )}

        <Box>
          <Button
            variant="contained"
            onClick={handleMatch}
            disabled={emails.length === 0 || tooManyEmails || matching}
          >
            {matching ? "Matching…" : "🔍 Preview & Match"}
          </Button>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {match && (
          <Box>
            <Alert severity="success" sx={{ mb: 1 }}>
              {match.matched.length} email{match.matched.length === 1 ? "" : "s"} matched to members
            </Alert>
            {match.notFound.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" gutterBottom>
                  {match.notFound.length} not reachable — no member with that email, or the account
                  is not active — please review:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {match.notFound.map((email) => (
                    <Chip key={email} label={email} size="small" color="warning" />
                  ))}
                </Stack>
              </Alert>
            )}
          </Box>
        )}

        {match && match.matched.length > 0 && (
          <Stack direction="row" spacing={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              onClick={handleAdd}
              disabled={adding || channelId.trim().length === 0}
            >
              ▶ {actionLabel} {match.matched.length} members
            </Button>
            {adding && <CircularProgress size={20} />}
          </Stack>
        )}

        {result?.sendId && (
          <BulkSendProgressPanel
            sendId={result.sendId}
            verb={actionLabel === "Invite" ? "Invited" : "Added"}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default AddMembersPanel;
